import { ImageEditor } from 'components/ImageEditor'
import { PPDialog } from 'components/PPDialog'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import styles from './PhotoEditor.pcss'

interface PhotoEditorDialogProps {
  open: boolean
  editId: string
  image?: string | File
  fileName?: string
  onExport: (file: File, editId: string) => void
  onError: (error?: any) => void
  onClose: () => void
}

export function PhotoEditorDialog(props: PhotoEditorDialogProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const dispatch = useDispatch()
  const intl = useIntl()
  // EXPL: keep latest onError in a ref so the image-load effect stays keyed on props.image only
  // (it sets a cache-busting URL on each run, so it must not re-fire on an unstable onError prop).
  const onErrorRef = useRef(props.onError)
  useEffect(() => {
    onErrorRef.current = props.onError
  })

  useEffect(() => {
    const image = props.image
    if (!image) {
      return
    }

    if (image instanceof File) {
      const reader = new FileReader()
      reader.onload = () => {
        setImageSrc(reader.result as string)
      }
      reader.onerror = () => {
        setImageSrc(null)
        onErrorRef.current(new Error('File could not be loaded.'))
      }
      reader.readAsDataURL(image)
    } else {
      // EXPL: add a random parameter to the url to force-fetch the image instead of loading it from browser cache.
      const url = image.indexOf('?') === -1
        ? `${image}?v=${Date.now()}`
        : `${image}&v=${Date.now()}`
      setImageSrc(url)
    }
  }, [props.image])

  const close = () => {
    setImageSrc(null)
    props.onClose()
  }

  const exportFile = (file: File) => {
    props.onExport(file, props.editId)
    close()
  }

  return (
    <PPDialog
      open={props.open}
      onClose={close}
      title="Edit photo"
      className={styles.dialog}
      data-testid="photo-editor-dialog"
    >
      <div className={styles['dialog-content']}>
        {imageSrc && (
          <ImageEditor
            imageUrl={imageSrc}
            className={styles['editor-box']}
            onExport={exportFile}
            onLoadError={props.onError}
            onProcessError={props.onError}
          />
        )}
      </div>
    </PPDialog>
  )
}
